import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, ReceiptText, Undo2 } from "lucide-react";
import { useState } from "react";

import {
  Button,
  Card,
  Dialog,
  EmptyState,
  Pill,
  SelectField,
  SkeletonRows,
  TextField,
} from "@/components/ui";
import { formatDate, formatMoney } from "@/lib/format";
import { expensesRepository, type Expense } from "@/repositories/expensesRepository";

/**
 * What the shop pays out.
 *
 * There is no edit button and no delete button, and their absence is the design.
 * An expense is a record of where money went; correcting it by overwriting would
 * leave a shop's own books unable to answer "what did we actually pay in March".
 * A mistake is undone by a reversal that keeps both entries and says why.
 *
 * The month total sits at the top rather than the bottom, because "what have I
 * spent this month" is the question somebody opens this screen with, and a
 * figure they have to scroll to find is a figure they will work out on paper
 * instead.
 */
export function ExpensesPage() {
  const [adding, setAdding] = useState(false);
  const [reversing, setReversing] = useState<Expense | null>(null);
  const [month, setMonth] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  });

  const queryClient = useQueryClient();
  const range = monthRange(month);

  const expenses = useQuery({
    queryKey: ["expenses", range],
    queryFn: () => expensesRepository.list(range),
  });

  const rows = expenses.data ?? [];
  const total = rows.reduce((sum, row) => sum + Number(row.amount), 0);

  function refresh() {
    setAdding(false);
    setReversing(null);
    void queryClient.invalidateQueries({ queryKey: ["expenses"] });
    // The profit-and-loss report and the dashboard both read expenses.
    void queryClient.invalidateQueries({ queryKey: ["reports"] });
    void queryClient.invalidateQueries({ queryKey: ["analytics"] });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <TextField
          label="Month"
          type="month"
          value={month}
          className="w-44"
          onChange={(event) => setMonth(event.target.value)}
        />
        <p className="flex-1 text-sm text-ink-600">
          <span className="text-lg font-semibold tabular-nums text-ink-900">
            {formatMoney(total.toFixed(2))}
          </span>{" "}
          across {rows.length} {rows.length === 1 ? "entry" : "entries"}
        </p>
        <Button variant="primary" onClick={() => setAdding(true)}>
          <Plus size={16} aria-hidden /> Record a cost
        </Button>
      </div>

      <Card>
        {expenses.isLoading ? (
          <SkeletonRows rows={6} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={ReceiptText}
            title="Nothing recorded this month"
            description="Rent, electricity, wages, transport. Stock does not belong here — it reaches the books through the cost of what you sell."
          />
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((expense) => (
              <li key={expense.id} className="flex flex-wrap items-center gap-3 py-3">
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-ink-900">{expense.description}</span>
                    <Pill tone="neutral">{expense.category_name}</Pill>
                    {expense.is_reversal && <Pill tone="warn">Reversal</Pill>}
                  </span>
                  <span className="block text-xs text-ink-500">
                    {formatDate(expense.incurred_on)}
                    {expense.reference && ` · ${expense.reference}`}
                    {expense.recorded_by_name && ` · ${expense.recorded_by_name}`}
                  </span>
                </span>

                <span
                  className={
                    Number(expense.amount) < 0
                      ? "text-sm tabular-nums text-success-700"
                      : "text-sm tabular-nums text-ink-900"
                  }
                >
                  {formatMoney(expense.amount)}
                </span>

                {!expense.is_reversal && (
                  <Button variant="ghost" size="sm" onClick={() => setReversing(expense)}>
                    <Undo2 size={14} aria-hidden /> Reverse
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {adding && <RecordDialog onClose={() => setAdding(false)} onSaved={refresh} />}
      {reversing && (
        <ReverseDialog
          expense={reversing}
          onClose={() => setReversing(null)}
          onReversed={refresh}
        />
      )}
    </div>
  );
}

function RecordDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [incurredOn, setIncurredOn] = useState(() => {
    // Local parts, not `toISOString` — see `monthRange`.
    const today = new Date();
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  });
  const [reference, setReference] = useState("");

  const categories = useQuery({
    queryKey: ["expenses", "categories"],
    queryFn: expensesRepository.categories,
  });

  const record = useMutation({
    mutationFn: () =>
      expensesRepository.record({
        category_id: category,
        amount,
        description: description.trim(),
        incurred_on: incurredOn,
        reference: reference.trim(),
      }),
    onSuccess: onSaved,
  });

  // The category is not defaulted. Defaulting to whichever heading happens to
  // sort first files an electricity bill under Rent when somebody taps straight
  // past it — and the headings are the entire point of the expenses report.
  const valid = category !== "" && Number(amount) > 0 && description.trim().length > 0;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Record a cost"
      description="Rent, electricity, wages, transport — anything that is not stock."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={record.isPending}
            disabled={!valid}
            onClick={() => record.mutate()}
          >
            Record
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <SelectField
          label="What kind of cost?"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          options={[
            { value: "", label: "Choose a heading…" },
            ...(categories.data ?? []).map((row) => ({ value: row.id, label: row.name })),
          ]}
        />
        <TextField
          label="Amount"
          value={amount}
          inputMode="decimal"
          className="w-40"
          onChange={(event) => setAmount(event.target.value)}
          placeholder="0.00"
        />
        <TextField
          label="What was it for?"
          value={description}
          maxLength={200}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="August rent"
        />
        <TextField
          label="Date paid"
          type="date"
          value={incurredOn}
          className="w-44"
          onChange={(event) => setIncurredOn(event.target.value)}
          hint="The day the money went, not the day you are entering it."
        />
        <TextField
          label="Reference"
          value={reference}
          maxLength={60}
          onChange={(event) => setReference(event.target.value)}
          placeholder="Invoice number, or who was paid"
        />

        {record.error != null && (
          <p className="text-sm text-danger-700">
            {record.error instanceof Error ? record.error.message : "That could not be recorded."}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function ReverseDialog({
  expense,
  onClose,
  onReversed,
}: {
  expense: Expense;
  onClose: () => void;
  onReversed: () => void;
}) {
  const [reason, setReason] = useState("");

  const reverse = useMutation({
    mutationFn: () => expensesRepository.reverse(expense.id, reason.trim()),
    onSuccess: onReversed,
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Reverse this cost"
      description="The original stays exactly as it was recorded. This writes its opposite."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={reverse.isPending}
            disabled={reason.trim().length === 0}
            onClick={() => reverse.mutate()}
          >
            Reverse {formatMoney(expense.amount)}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-ink-600">
          {expense.description} · {formatDate(expense.incurred_on)}
        </p>
        <TextField
          label="Why?"
          value={reason}
          maxLength={60}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Entered twice"
        />

        {reverse.error != null && (
          <p className="text-sm text-danger-700">
            {reverse.error instanceof Error ? reverse.error.message : "That could not be reversed."}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/**
 * First and last day of a `YYYY-MM` month, as the API wants them.
 *
 * Built from local date parts rather than `toISOString`, which converts to UTC
 * first — in SAST that moves the first of the month into the previous one and a
 * whole day of spending disappears from the total.
 */
function monthRange(month: string): { from: string; to: string } {
  const [year = 0, index = 1] = month.split("-").map(Number);
  const last = new Date(year, index, 0).getDate();
  const pad = (value: number) => String(value).padStart(2, "0");
  return {
    from: `${year}-${pad(index)}-01`,
    to: `${year}-${pad(index)}-${pad(last)}`,
  };
}
